/**
 * Allowed admin status transitions — must stay in sync with
 * apps.dashboard.services.order_management.validate_admin_order_status_transition
 */
import type { OrderStatus } from '../types/order';

const MAIN_FLOW_RANK: Record<string, number> = {
  PENDING: 0,
  CONFIRMED: 1,
  ASSIGNED: 2,
  IN_PROGRESS: 3,
  COMPLETED: 4,
};

const FORWARD_STATUSES: OrderStatus[] = ['CONFIRMED', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED'];

/**
 * Returns statuses the admin may set from the current status (excludes "same" status).
 * Empty array means no changes are allowed (terminal order).
 */
export function getAllowedNextOrderStatuses(current: OrderStatus): OrderStatus[] {
  if (current === 'CANCELLED' || current === 'REJECTED' || current === 'REFUNDED') {
    return [];
  }
  if (current === 'COMPLETED') {
    return ['REFUNDED'];
  }

  const oldRank = MAIN_FLOW_RANK[current];
  if (oldRank === undefined) {
    return [];
  }

  const forward = FORWARD_STATUSES.filter((s) => MAIN_FLOW_RANK[s] > oldRank);
  return [...forward, 'CANCELLED', 'REJECTED'];
}

/** RTK Query / DRF error shape for order status PATCH */
export function formatOrderStatusUpdateError(err: unknown): string {
  const e = err as { data?: { message?: string; errors?: Record<string, string | string[]> } };
  const errors = e?.data?.errors;
  if (errors && typeof errors === 'object') {
    const servicePriceErr = errors.service_price;
    if (servicePriceErr !== undefined) {
      return Array.isArray(servicePriceErr) ? String(servicePriceErr[0]) : String(servicePriceErr);
    }
    const platformFeeErr = errors.platform_fee;
    if (platformFeeErr !== undefined) {
      return Array.isArray(platformFeeErr) ? String(platformFeeErr[0]) : String(platformFeeErr);
    }
    const statusErr = errors.status;
    if (statusErr !== undefined) {
      return Array.isArray(statusErr) ? String(statusErr[0]) : String(statusErr);
    }
  }
  if (e?.data?.message) {
    return String(e.data.message);
  }
  return 'Failed to update order status';
}

/** Parse admin delivery fee (platform_fee) input; returns "0.00" style string or null if invalid. */
export function normalizePlatformFeeInput(raw: string): string | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0) return null;
  return n.toFixed(2);
}

/** Parse admin service price input; must be greater than 0. Returns "0.00" style string or null if invalid. */
export function normalizeServicePriceInput(raw: string): string | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) return null;
  return n.toFixed(2);
}

/** Backend requires platform_fee (delivery fee in UI) when moving into CONFIRMED from any other status. */
export function orderTransitionRequiresPlatformFee(
  fromStatus: OrderStatus,
  toStatus: OrderStatus
): boolean {
  return toStatus === 'CONFIRMED' && fromStatus !== 'CONFIRMED';
}

export function splitAllowedStatusesForSelect(allowed: OrderStatus[]): {
  forward: OrderStatus[];
  terminal: OrderStatus[];
} {
  const terminal: OrderStatus[] = [];
  const forward: OrderStatus[] = [];
  for (const s of allowed) {
    if (s === 'CANCELLED' || s === 'REJECTED') {
      terminal.push(s);
    } else {
      forward.push(s);
    }
  }
  return { forward, terminal };
}
